import React from 'react';
import { Button, Dialog, DialogBody, DialogSurface, DialogTitle, DialogTrigger, tokens } from '@fluentui/react-components';
import { DialogContent, IStyle, memoizeFunction } from '@fluentui/react';
import { AttachmentMetadata } from '@azure/communication-react';
import { isImageFile, IsPdfFile } from '../../../utils';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { PdfAttachmentRenderer } from './PdfAttachmentRenderer';
import { ImageAttachmentRenderer } from './ImageAttachmentRenderer';
import { DialogOpenChangeData, DialogOpenChangeEvent } from '@fluentui/react-dialog';
import { initializeFileTypeIcons } from '@fluentui/react-file-type-icons';
import { selectIsMobile, usePcpLiveLessonContext } from '../../../store';

const SURFACE_PADDING = '24px';     //taken from '@fluentui/react-dialog' lib
const SURFACE_BORDER_WIDTH = '1px'; //taken from '@fluentui/react-dialog' lib

// Dialog surface will fit the content.
// Used for smaller attachments like images.
const dialogSurfaceFitStyles = memoizeFunction((isMobile: boolean): React.CSSProperties => ({
    maxWidth: 'fit-content',
    borderRadius: isMobile ? 0 : tokens.borderRadiusXLarge, //borderRadiusXLarge is the default value from ACS default theme
}));

// Large dialog surface for larger attachments like PDFs.
const dialogSurfaceLargeStyles = memoizeFunction((isMobile: boolean): React.CSSProperties => ({
    width: isMobile ? '100dvw' : '90vw',
    maxWidth: isMobile ? '100dvw' : '90vw',
    height: '100dvh',
    borderRadius: isMobile ? 0 : tokens.borderRadiusXLarge, //borderRadiusXLarge is the default value from ACS default theme
}));

const dialogBodyStyles = {
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto minmax(0,1fr)',
    width: '100%',
    maxHeight: `calc(100dvh - ${SURFACE_PADDING} * 2 - ${SURFACE_BORDER_WIDTH} * 2)`,
};

const dialogContentStyles = memoizeFunction((isMobile: boolean): IStyle => ({
    width: isMobile ? 'auto' : 'fit-content',
    overflow: 'visible',
    maxWidth: '100%',

    display: 'table-cell',
    gridColumn: '1 / 4',
}));

const dialogHeaderStyles: IStyle = {
    display: 'none',
};




// TODO: move up in the tree
initializeFileTypeIcons();

interface ChatFilePreviewDialogProps {
    open: boolean;
    attachment?: AttachmentMetadata;
    onClose: () => void;
}

/**
 * Preview uploaded chat file attachments in a dialog.
 * Supported file types are
 * - Images
 * - PDFs
 */
export const ChatFilePreviewDialog = (props: ChatFilePreviewDialogProps): React.ReactElement => {
    const { open, onClose, attachment } = props;
    const isMobile = usePcpLiveLessonContext(selectIsMobile);

    const onOpenChange = React.useCallback(
        (event: DialogOpenChangeEvent, data: DialogOpenChangeData) => {
            if (!data.open) {
                onClose();
            }
        },
        [onClose]
    );

    const dialogSurfaceStyles = React.useMemo(() => attachment && IsPdfFile(attachment.name)
        ? dialogSurfaceLargeStyles(isMobile)
        : dialogSurfaceFitStyles(isMobile), [attachment, isMobile]);

    const dialogInnerContentStyles = React.useMemo((): IStyle => {
        const isMaximizeDialog: boolean | undefined = attachment && IsPdfFile(attachment.name);
        return isMaximizeDialog ? ({
            flexGrow: 1,    //makes 'max-height: 100%' (via "flex-grow")
            overflow: 'hidden',     //scrollbars defined inside PdfAttachmentRenderer component.
            display: 'flex',    //setting child height: 100%
            flexDirection: 'column',
        }) : ({
            flexGrow: 1,    //makes 'max-height: 100%' (via "flex-grow")
            overflow: 'auto',   //show scrollbars on current element.
        });
    }
    , [attachment]);

    return <Dialog
        open={open}
        onOpenChange={onOpenChange}
    >
        <DialogSurface style={dialogSurfaceStyles}>
            <DialogBody style={dialogBodyStyles}>
                <DialogTitle
                    action={
                        <DialogTrigger action="close">
                            <Button
                                appearance="subtle"
                                aria-label="close"
                                icon={<Dismiss24Regular />}
                            />
                        </DialogTrigger>
                    }
                >
                    {attachment?.name}
                </DialogTitle>
                <DialogContent styles={{
                    content: dialogContentStyles(isMobile), 
                    header: dialogHeaderStyles, 
                    inner: { 
                        padding: 0,
                        maxHeight: '100%',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                    innerContent: dialogInnerContentStyles
                }}>
                    {attachment && <FileAttachmentRenderer attachment={attachment} isMobile={isMobile} />}
                </DialogContent>
            </DialogBody>
        </DialogSurface>
    </Dialog>
};

const FileAttachmentRenderer = (props: { attachment: AttachmentMetadata, isMobile: boolean }) => {
    const { attachment, isMobile } = props;

    if (isImageFile(attachment.name)) {
        return <ImageAttachmentRenderer attachment={attachment} />;
    }

    if (IsPdfFile(attachment.name)) {
        return <PdfAttachmentRenderer attachment={attachment} isMobile={isMobile} />;
    }

    return <iframe src={attachment.url} title={attachment.name} width={'100%'} height={'100%'} />;
};

